import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, X, Loader2, FolderKanban, Search, Pencil, Trash2, Save,
} from 'lucide-react';
import { api } from '@/utils/api';
import { loadAllPages } from '@/utils/loadAllPages';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { formatINR, formatDate } from '@/utils/formatters';

interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  progress?: number;
  startDate?: string;
  deadline?: string;
  budget?: number;
  account?: { id: string; name: string } | null;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  not_started: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Not Started' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'In Progress' },
  on_hold: { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'On Hold' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', label: 'Completed' },
};

const PROJECT_STATUSES = ['not_started', 'in_progress', 'on_hold', 'completed'];

export function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // Add Project Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectAccounts, setProjectAccounts] = useState<{ id: string; name: string }[]>([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    deadline: '',
    budget: '',
    accountId: '',
  });

  // Edit Project Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    description: '',
    status: 'not_started',
    startDate: '',
    deadline: '',
    budget: '',
    progress: '',
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      const res = await api.get('/projects', { params });
      setProjects(res.data.data?.projects ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Picker loads every page (SUN-286): a plain /accounts call showed only the first 20.
  useEffect(() => {
    const ctl = new AbortController();
    loadAllPages('/accounts', ctl.signal, { label: 'Account' }).then(setProjectAccounts).catch(() => {});
    return () => ctl.abort();
  }, []);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/projects', {
        name: form.name,
        description: form.description || undefined,
        startDate: form.startDate || undefined,
        deadline: form.deadline || undefined,
        budget: form.budget ? parseFloat(form.budget) : undefined,
        accountId: form.accountId || undefined,
      });
      setShowModal(false);
      setForm({ name: '', description: '', startDate: new Date().toISOString().split('T')[0], deadline: '', budget: '', accountId: '' });
      fetchProjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.delete(`/projects/${id}`);
      fetchProjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete project');
    }
  };

  const openEditModal = (project: Project) => {
    setEditForm({
      id: project.id,
      name: project.name,
      description: project.description || '',
      status: project.status || 'not_started',
      startDate: project.startDate ? project.startDate.split('T')[0] : '',
      deadline: project.deadline ? project.deadline.split('T')[0] : '',
      budget: project.budget != null ? String(project.budget) : '',
      progress: project.progress != null ? String(project.progress) : '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    try {
      setEditSaving(true);
      await api.put(`/projects/${editForm.id}`, {
        name: editForm.name,
        description: editForm.description || undefined,
        status: editForm.status,
        startDate: editForm.startDate || undefined,
        deadline: editForm.deadline || undefined,
        budget: editForm.budget ? parseFloat(editForm.budget) : undefined,
        progress: editForm.progress ? parseFloat(editForm.progress) : undefined,
      });
      setShowEditModal(false);
      fetchProjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update project');
    } finally {
      setEditSaving(false);
    }
  };

  const getStatus = (status: string) => STATUS_COLORS[status] || STATUS_COLORS.not_started;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm text-gray-500">Track and manage your projects</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Add Project
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading projects...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchProjects} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <FolderKanban size={40} className="mb-2" />
          <p className="text-sm">No projects found</p>
          <p className="text-xs">Create your first project to get started</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map(project => {
            const st = getStatus(project.status);
            const progress = project.progress ?? 0;
            return (
              <div
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="rounded-xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="mb-3 flex items-start justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 leading-tight">{project.name}</h3>
                  <span className={`inline-flex flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${st.bg} ${st.text}`}>
                    {st.label}
                  </span>
                </div>

                {project.description && (
                  <p className="mb-3 text-xs text-gray-500 line-clamp-2">{project.description}</p>
                )}

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-gray-500">Progress</span>
                    <span className="font-medium text-gray-700">{progress}%</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100">
                    <div
                      className="h-1.5 rounded-full bg-[var(--ink-0)] transition-all"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div>
                    {project.deadline && (
                      <span>Due: {formatDate(project.deadline)}</span>
                    )}
                  </div>
                  <div>
                    {project.account && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {project.account.name}
                      </span>
                    )}
                  </div>
                </div>

                {project.budget != null && project.budget > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Budget: <span className="font-medium text-gray-700">{formatINR(project.budget)}</span>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-end gap-2 border-t border-gray-100 pt-2">
                  <button onClick={(e) => { e.stopPropagation(); openEditModal(project); }} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 mt-4 rounded-lg border border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* Add Project Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)} className="!max-w-lg !bg-white">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Project</h2>
              <RequestCloseButton  className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Project Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Deadline</label>
                  <input
                    type="date"
                    value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Budget (INR)</label>
                <input
                  type="number"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  placeholder="0"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                <select
                  value={form.accountId}
                  onChange={(e) => setForm({ ...form, accountId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select Account</option>
                  {projectAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton
                
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleCreate}
                disabled={saving || !form.name}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Project
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <Modal open onClose={() => setShowEditModal(false)} className="!max-w-lg !bg-white">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Project</h2>
              <RequestCloseButton  className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Project Name *</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {PROJECT_STATUSES.map(s => <option key={s} value={s}>{STATUS_COLORS[s]?.label || s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Progress (%)</label>
                  <input type="number" value={editForm.progress} onChange={e => setEditForm({ ...editForm, progress: e.target.value })} min="0" max="100"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
                  <input type="date" value={editForm.startDate} onChange={e => setEditForm({ ...editForm, startDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Deadline</label>
                  <input type="date" value={editForm.deadline} onChange={e => setEditForm({ ...editForm, deadline: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Budget (INR)</label>
                <input type="number" value={editForm.budget} onChange={e => setEditForm({ ...editForm, budget: e.target.value })} placeholder="0"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleEdit} disabled={editSaving || !editForm.name}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
